'use client';
import React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { LazyMotion } from 'motion/react';
import { UserProvider } from '../context/UserContext';
import { FetchProvider } from '../context/FetchProvider';
import { SnackbarProvider } from '../context/SnackbarProvider';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import QueueProvider from '../context/QueueProvider';
import SearchProvider from '../context/SearchProvider';
import PlaylistProvider from '../context/PlaylistProvider';
import { TopicsProvider } from '../context/TopicsContext';
import { PageProps } from '../types/page';
import { FeatureFlagsProvider } from '../context/FeatureFlagsContext';
import PageChildren from './PageChildren';
import FreeTrialActivatedModal from './dashboard/FreeTrialActivatedModal';
import { OnboardingChecklistProvider } from '../context/OnboardingChecklistProvider';
import { LanguageProvider } from '../context/LanguageProvider';
import Header from './header/Header';
import PageInitializers from '../context/PageInitializers';
import { UserToolsProvider } from '../context/UserToolsProvider';
import SaleBannerWrapper from './buy/components/SaleBannerWrapper';
import { EmbedProvider } from '@osmosis/nextjs-components/context/EmbedProvider';
import { DeviceTypeProvider } from '@osmosis/nextjs-components/context/DeviceTypeProvider';

const loadFeatures = () =>
  import('../lib/motionFeatures').then((res) => res.default);

// Init query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

type Props = PageProps;

/**
 * Component with providers.
 */
const PageContent = (props: Props): JSX.Element => {
  const {
    user: initialUser,
    bestLanguage,
    children,
    intlMessages: serverIntlMessages,
    hasServerTranslationsFilter,
    containerProps = {},
    auth = {},
    showHeader = true,
    headerSearchVisible = true,
    featureFlags = [],
    searchResultsProps,
    startedFreeTrial,
    loadUserOnClient,
    isLoggedIn,
    hasEolsToken,
    shouldHandleLanguageCookie,
    isIframe,
  } = props;

  return (
    <LazyMotion strict features={loadFeatures}>
      <FetchProvider csrfToken={auth.csrfToken}>
        <QueryClientProvider client={queryClient}>
          <FeatureFlagsProvider featureFlags={featureFlags}>
            <DeviceTypeProvider>
              <EmbedProvider isEmbed={isIframe}>
                <UserProvider
                  user={initialUser}
                  isLoggedIn={isLoggedIn}
                  loadUserOnClient={loadUserOnClient}
                  hasEolsToken={hasEolsToken}
                >
                  <PageInitializers
                    user={initialUser}
                    disableThirdPartyInitializers={isIframe}
                  >
                    <LanguageProvider
                      user={initialUser}
                      bestLanguage={bestLanguage}
                      serverIntlMessages={serverIntlMessages}
                      hasServerTranslationsFilter={hasServerTranslationsFilter}
                      shouldHandleLanguageCookie={shouldHandleLanguageCookie}
                    >
                      <TopicsProvider>
                        <UserToolsProvider>
                          <QueueProvider>
                            <SearchProvider>
                              <SnackbarProvider role="status">
                                <PlaylistProvider>
                                  <OnboardingChecklistProvider>
                                    {(showHeader || startedFreeTrial) && (
                                      <header>
                                        {startedFreeTrial && (
                                          <FreeTrialActivatedModal />
                                        )}
                                        {showHeader && (
                                          <Header
                                            headerSearchVisible={
                                              headerSearchVisible
                                            }
                                            isLoggedIn={isLoggedIn}
                                          />
                                        )}
                                        {showHeader && <SaleBannerWrapper />}
                                      </header>
                                    )}
                                    {isIframe ? (
                                      children
                                    ) : (
                                      <main>
                                        <Box
                                          style={{ scrollBehavior: 'smooth' }}
                                        >
                                          <Flex {...containerProps}>
                                            <PageChildren
                                              searchResultsProps={
                                                searchResultsProps
                                              }
                                            >
                                              {children}
                                            </PageChildren>
                                          </Flex>
                                        </Box>
                                      </main>
                                    )}
                                  </OnboardingChecklistProvider>
                                </PlaylistProvider>
                              </SnackbarProvider>
                            </SearchProvider>
                          </QueueProvider>
                        </UserToolsProvider>
                      </TopicsProvider>
                    </LanguageProvider>
                  </PageInitializers>
                </UserProvider>
              </EmbedProvider>
            </DeviceTypeProvider>
          </FeatureFlagsProvider>
        </QueryClientProvider>
      </FetchProvider>
    </LazyMotion>
  );
};

export default PageContent;
